import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';
import { trainingsDB } from '../../lib/db/trainings';
import { schoolsDB } from '../../lib/db/schools';
import { trackEvent, EVENTS } from '../../lib/analytics';
import CompletedDrillItem from './CompletedDrillItem';
import FocusSelector from './FocusSelector';
import { format, parseISO } from 'date-fns';
import Modal from '../common/Modal';

export default function SessionDetailsModal({ 
  isOpen, 
  session, 
  onClose,
  onUpdate 
}) {
  const [editingDrills, setEditingDrills] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');
  const [editDrillState, setEditDrillState] = useState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [], drillCategory: null });
  const [schoolData, setSchoolData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  const { currentUser } = useAuth();
  const { userProfile } = useUserProfile();
  const { t, i18n } = useTranslation();

  // Fetch school data
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!userProfile?.profile?.schoolId) return;
      
      try {
        const school = await schoolsDB.getSchool(userProfile.profile.schoolId);
        setSchoolData(school);
      } catch (err) {
        console.error('Error fetching school data:', err);
      }
    };
    
    fetchSchoolData();
  }, [userProfile]);

  // Initialize state when session changes
  useEffect(() => {
    if (session) {
      setEditingDrills([...session.drills]);
      setSessionNotes(session.sessionNotes || '');
      setHasChanges(false);
    }
  }, [session]);

  const showModal = (title, message, onConfirm = null, type = 'alert', variant = 'primary') => {
    setModalState({ isOpen: true, title, message, onConfirm, type, variant });
  };

  const hideModal = () => {
    setModalState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    hideModal();
  };

  const handleClose = useCallback(() => {
    if (hasChanges) {
      showModal(
        t('progress.unsaved_changes'),
        t('progress.unsaved_changes_message'),
        () => {
          setHasChanges(false);
          onClose();
        },
        'confirm',
        'warning'
      );
    } else {
      onClose();
    }
  }, [hasChanges, onClose, t]);

  // Close on Escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (hasChanges) {
          showModal(
            t('progress.unsaved_changes'),
            t('progress.unsaved_changes_message'),
            () => {
              handleClose();
            },
            'confirm',
            'warning'
          );
        } else {
          handleClose();
        }
      }
    };
    
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges, handleClose, t]);

  const handleEditDrill = async (drill, drillIndex) => {
    const minutes = Math.floor(drill.durationSeconds / 60);
    const seconds = drill.durationSeconds % 60;
    const selectedFocusIds = drill.focus || [];
    
    // Fetch drill to get category for focus selection
    let drillCategory = null;
    if (userProfile?.profile?.schoolId && drill.drillId && drill.drillId !== 'custom') {
      try {
        const drillData = await schoolsDB.getDrill(userProfile.profile.schoolId, drill.drillId);
        drillCategory = drillData?.category || null;
      } catch (err) {
        console.error('Error fetching drill for category:', err);
      }
    }
    
    setEditDrillState({ isOpen: true, drillIndex, minutes, seconds, selectedFocusIds, drillCategory });
  };

  const handleCloseEditDrill = () => {
    setEditDrillState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [], drillCategory: null });
  };

  const handleSaveDrillEdit = () => {
    const { drillIndex, minutes, seconds, selectedFocusIds } = editDrillState;
    if (drillIndex === null) return;
    
    const newDurationSeconds = minutes * 60 + seconds;
    if (newDurationSeconds <= 0) {
      showModal(
        t('training.edit_drill_invalid_time'),
        t('training.edit_drill_invalid_time_message'),
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    setEditingDrills(prev => {
      const updated = prev.map((drill, idx) => {
        if (idx === drillIndex) {
          // First edit: set originDurationSeconds to current durationSeconds
          // Subsequent edits: preserve originDurationSeconds
          const originDurationSeconds = drill.originDurationSeconds !== undefined 
            ? drill.originDurationSeconds 
            : drill.durationSeconds;
          
          return {
            ...drill,
            durationSeconds: newDurationSeconds,
            originDurationSeconds,
            focus: selectedFocusIds.length > 0 ? [...selectedFocusIds] : []
          };
        }
        return drill;
      });
      setHasChanges(true);
      return updated;
    });
    
    handleCloseEditDrill();
  };

  // Get applicable focus items for a drill by category
  const getApplicableFocusForCategory = (category) => {
    if (!schoolData?.focus || !category) return [];
    
    return schoolData.focus.filter(focusItem => 
      focusItem.categories && focusItem.categories.includes(category)
    );
  };

  const handleDeleteDrill = () => {
    const { drillIndex } = editDrillState;
    if (drillIndex === null) return;
    
    showModal(
      t('training.delete_drill_confirm'),
      t('training.delete_drill_confirm_message'),
      () => {
        setEditingDrills(prev => {
          const updated = prev.filter((_, idx) => idx !== drillIndex);
          setHasChanges(true);
          return updated;
        });
        handleCloseEditDrill();
      },
      'confirm',
      'danger'
    );
  };

  const handleSave = async () => {
    if (editingDrills.length === 0) {
      showModal(
        t('training.no_drills_error'),
        t('training.no_drills_error_message') || '',
        null,
        'alert',
        'warning'
      );
      return;
    }

    setSaving(true);
    
    try {
      await trainingsDB.updateTraining(currentUser.uid, session.id, {
        drills: editingDrills,
        sessionNotes: sessionNotes.trim() || null
      });
      
      trackEvent(EVENTS.TRAINING_SESSION_EDITED, {
        sessionId: session.id,
        drillCount: editingDrills.length
      });
      
      setHasChanges(false);
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      console.error('Error updating training:', err);
      showModal(
        t('error.server_error'),
        err.message,
        null,
        'alert',
        'danger'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatSessionTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !session) return null;

  const totalDuration = editingDrills.reduce((sum, d) => sum + d.durationSeconds, 0);

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
        onClick={handleClose}
      >
        {/* Backdrop with blur */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        
        {/* Modal content */}
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col transform animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                  {t('progress.session_details')}
                </h3>
                <div className="text-sm md:text-base text-gray-600 mt-1">
                  {format(parseISO(session.startedAt), 'MMM dd, yyyy HH:mm')}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                title={t('common.close')}
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
            {/* Session Timer */}
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
              <div className="text-center">
                <div className="text-gray-600 text-sm md:text-xl font-semibold mb-2">
                  {t('training.total_time')}
                </div>
                <div className="text-2xl md:text-4xl font-mono font-bold text-primary-600">
                  {formatSessionTime(totalDuration)}
                </div>
                <div className="text-xs md:text-sm text-gray-600 mt-2">
                  {editingDrills.length} {t('progress.drills')}
                </div>
              </div>
            </div>

            {/* Completed Drills */}
            {editingDrills.length > 0 ? (
              <div className="bg-white rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-200">
                <h3 className="font-bold mb-3 md:mb-4 text-base md:text-xl text-gray-800">
                  {t('training.completed_drills')} ({editingDrills.length})
                </h3>
                <div className="space-y-2 md:space-y-3">
                  {editingDrills.map((drill, idx) => (
                    <CompletedDrillItem
                      key={idx}
                      drill={drill}
                      index={idx}
                      onEdit={handleEditDrill}
                      continueDrillEnabled={false}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 text-center text-gray-500">
                {t('progress.no_drills_in_session')}
              </div>
            )}
            
            {/* Session Notes */}
            <div className="mb-4 md:mb-6">
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2">
                {t('training.session_notes')}
              </label>
              <textarea
                value={sessionNotes}
                onChange={(e) => {
                  setSessionNotes(e.target.value);
                  setHasChanges(true);
                }}
                className="input text-sm md:text-base resize-none w-full"
                rows={3}
                placeholder={t('training.session_notes_placeholder')}
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
            <button
              onClick={handleClose}
              className="px-4 md:px-6 py-2 md:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95 flex-1"
            >
              {hasChanges ? t('common.cancel') : t('common.close')}
            </button>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving || editingDrills.length === 0}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Edit Drill Popup */}
      {editDrillState.isOpen && editDrillState.drillIndex !== null && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
          onClick={handleCloseEditDrill}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          {/* Popup content */}
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                  {t('training.edit_drill')}
                </h3>
                <button
                  onClick={handleCloseEditDrill}
                  className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                  title={t('common.close')}
                >
                  ×
                </button>
              </div>
              {editingDrills[editDrillState.drillIndex] && (
                <p className="text-sm md:text-lg text-gray-600 mt-2">
                  {editingDrills[editDrillState.drillIndex].drillName}
                </p>
              )}
            </div>
            
            {/* Body */}
            <div className="px-4 md:px-6 py-4 md:py-6">
              <div className="mb-4 md:mb-6">
                <label className="block text-sm md:text-xl font-semibold text-gray-700 mb-2 md:mb-4">
                  {t('training.edit_drill_time')}
                </label>
                <div className="flex items-center gap-2 md:gap-4" style={{ direction: 'ltr' }}>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('training.minutes')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editDrillState.minutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, minutes: Math.max(0, value) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div className="pt-6 md:pt-8 text-xl md:text-3xl font-bold text-gray-400">:</div>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('common.seconds_short')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={editDrillState.seconds}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, seconds: Math.max(0, Math.min(59, value)) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>
              </div>
              
              {/* Focus Selection */}
              {(() => {
                const applicableFocus = editDrillState.drillCategory 
                  ? getApplicableFocusForCategory(editDrillState.drillCategory) 
                  : [];
                
                if (applicableFocus.length === 0) return null;
                
                return (
                  <div className="mb-4 md:mb-6">
                    <FocusSelector
                      applicableFocus={applicableFocus}
                      selectedFocusIds={editDrillState.selectedFocusIds}
                      onFocusChange={(newFocusIds) => {
                        setEditDrillState(prev => ({ ...prev, selectedFocusIds: newFocusIds }));
                      }}
                    />
                  </div>
                );
              })()}
            </div>
            
            {/* Actions */}
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 md:gap-3">
              <button
                onClick={handleDeleteDrill}
                className="px-4 md:px-6 py-2 md:py-3 border-2 border-red-500 text-red-600 rounded-xl font-semibold text-sm md:text-base hover:bg-red-50 active:bg-red-100 transition-all hover:scale-105 active:scale-95"
              >
                {t('training.delete_drill')}
              </button>
              <div className="flex gap-2 md:gap-3 flex-1">
                <button
                  onClick={handleCloseEditDrill}
                  className="px-4 md:px-6 py-2 md:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveDrillEdit}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={handleModalConfirm}
        onCancel={hideModal}
        type={modalState.type}
        variant={modalState.variant}
      />
    </>
  );
}

